import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template61: Página de Día libre de Tokio
// Copia DIRECTA del componente Page21.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template61 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [portadaError, setPortadaError] = useState(false);
	const [ikebukuroError, setIkebukuroError] = useState(false);
	const [skytreeError, setSkytreeError] = useState(false);
	const [logoError, setLogoError] = useState(false);
	const [freeDayStickerError, setFreeDayStickerError] = useState(false);
	const [pokemonError, setPokemonError] = useState(false);
	const [sushiStickerError, setSushiStickerError] = useState(false);

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const actualHeaderColor =
		isTokioGuide && pageOrder > 0 ? "#e24217" : headerBackgroundColor;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const portadaImage = getImageSrc("dia-libre");
	const skytreeImage = getImageSrc("skytree");
	const sushiStickerImage = getImageSrc("sushi-sticker");
	const ikebukuroImage = getImageSrc("ikebukuro");
	const pokemonImage = getImageSrc("pokemon-center");
	const freeDaySticker = getCanvasImageUrl("dia-libre-sticker");

	const heroTitle = page.texts?.[1]?.content;
	const introText = page.texts?.[2]?.content;
	const ikebukuroTitle = page.texts?.[3]?.content;
	const ikebukuroTextShort = page.texts?.[4]?.content;
	const ikebukuroTextLong = page.texts?.[5]?.content;
	const sabiasQueIkebukuroHtml = page.texts?.[6]?.content;
	const skytreeTitle = page.texts?.[7]?.content;
	const skytreeTextHtml = page.texts?.[8]?.content;
	const sabiasQueSkytreeHtml = page.texts?.[9]?.content;
	const stickerPokemonHtml = page.texts?.[10]?.content;
	const comprasHtml = page.texts?.[11]?.content;
	const sumoTitle = page.texts?.[12]?.content;
	const sumoTextHtml = page.texts?.[13]?.content;
	const stickerSushiHtml = page.texts?.[14]?.content;

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};
	const sabiasQueIkebukuro = parseSabiasQueItem(sabiasQueIkebukuroHtml);
	const sabiasQueSkytree = parseSabiasQueItem(sabiasQueSkytreeHtml);

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
		"& p": {
			marginTop: "0px !important",
			marginBottom: "0px !important",
		},
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#d2ebf0",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				".marginless": {
					marginBottom: "0px !important",
				},
			}}
		>
			{(headerText || logoImage) && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 800,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 1053,
					}}
				>
					{headerText}
					{logoImage && !logoError && (
						<Box
							component="img"
							src={logoImage}
							alt=""
							onError={() => setLogoError(true)}
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{(portadaImage || heroTitle || freeDaySticker) && (
				<Box data-block-id="image-0" sx={{ position: "relative" }}>
					{portadaImage &&
						(!portadaError ? (
							<img
								src={portadaImage}
								alt="Dia libre"
								onError={() => setPortadaError(true)}
								style={{ width: "100%", height: 200, objectFit: "cover" }}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="100%"
								height={200}
								fontFamily={fontFamily || "'Poppins', sans-serif"}
							/>
						))}
					{heroTitle && (
						<Box
							component="h1"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								fontSize: 32,
								position: "absolute",
								bottom: 0,
								left: "20px",
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
								margin: "10px",
							}}
						>
							{heroTitle}
						</Box>
					)}
					{freeDaySticker && !freeDayStickerError && (
						<img
							src={freeDaySticker}
							alt=""
							onError={() => setFreeDayStickerError(true)}
							style={{ width: 90, position: "absolute", bottom: 55, left: 20 }}
						/>
					)}
				</Box>
			)}

			{introText && (
				<Box data-block-id="text-1" sx={{ padding: "10px 12px" }}>
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
							fontSize: 13,
							color: "#333",
							margin: 0,
							marginBottom: "0px !important",
						}}
						className="marginless"
					>
						{introText}
					</Box>
				</Box>
			)}

			{/* BLOQUES */}
			<Box
				sx={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					paddingTop: 0,
					gap: isSmallScreen ? "14px" : "10px",
					marginBottom: "-18px",
				}}
			>
				{(ikebukuroTitle ||
					ikebukuroTextShort ||
					ikebukuroTextLong ||
					ikebukuroImage) && (
					<Box
						data-block-id="text-2"
						sx={{ display: "flex", gap: "16px", position: "relative" }}
						className="marginless"
					>
						{ikebukuroImage &&
							(!ikebukuroError ? (
								<img
									src={ikebukuroImage}
									alt="Ikebukuro"
									onError={() => setIkebukuroError(true)}
									style={{
										width: "40%",
										borderRadius: "12px",
										objectFit: "cover",
										height: 110,
									}}
								/>
							) : (
								<ImagenNoDisponibleKawaii
									width="40%"
									height={110}
									fontFamily={fontFamily || "'Poppins', sans-serif"}
									compact
								/>
							))}
						<Box sx={{ width: ikebukuroImage ? "60%" : "100%" }}>
							{ikebukuroTitle && (
								<Box component="h3" sx={estiloH3}>
									{ikebukuroTitle}
								</Box>
							)}
							{(ikebukuroTextShort || ikebukuroTextLong) && (
								<Box component="p" sx={estiloP}>
									{isSmallScreen ? ikebukuroTextShort : ikebukuroTextLong}
								</Box>
							)}
						</Box>
						{sabiasQueIkebukuro && (
							<Box sx={{ position: "absolute", top: 0, left: "33%" }}>
								<SabiasQue
									icono={sabiasQueIkebukuro.icono}
									texto={sabiasQueIkebukuro.texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{(skytreeTitle || skytreeTextHtml || skytreeImage) && (
					<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{skytreeImage &&
							(!skytreeError ? (
								<img
									src={skytreeImage}
									alt="Skytree"
									onError={() => setSkytreeError(true)}
									style={{
										width: "40%",
										borderRadius: "12px",
										objectFit: "cover",
										height: 110,
									}}
								/>
							) : (
								<ImagenNoDisponibleKawaii
									width="40%"
									height={110}
									fontFamily={fontFamily || "'Poppins', sans-serif"}
									compact
								/>
							))}
						<Box sx={{ width: skytreeImage ? "60%" : "100%" }}>
							{skytreeTitle && (
								<Box component="h3" sx={estiloH3}>
									{skytreeTitle}
								</Box>
							)}
							{skytreeTextHtml && (
								<Box
									component="p"
									sx={{
										...estiloP,
										"& a": {
											color: "#f28b35",
											fontWeight: 600,
											textDecoration: "underline",
										},
									}}
									dangerouslySetInnerHTML={{ __html: skytreeTextHtml }}
								/>
							)}
						</Box>
						{sabiasQueSkytree && (
							<Box sx={{ position: "absolute", top: 0, left: "35%" }}>
								<SabiasQue
									icono={sabiasQueSkytree.icono}
									texto={sabiasQueSkytree.texto}
								/>
							</Box>
						)}
						{pokemonImage && !pokemonError && (
							<img
								src={pokemonImage}
								alt=""
								onError={() => setPokemonError(true)}
								style={{
									width: 110,
									position: "absolute",
									bottom: isSmallScreen ? -160 : -30,
									left: -30,
								}}
							/>
						)}
						{stickerPokemonHtml && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? -80 : "50px",
									left: 0,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 16px 0px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 120,
								}}
								dangerouslySetInnerHTML={{ __html: stickerPokemonHtml }}
							/>
						)}
					</Box>
				)}

				{comprasHtml && (
					<Box
						data-block-id="text-4"
						sx={{
							display: "flex",
							justifyContent: isSmallScreen ? "flex-end" : "flex-start",
						}}
					>
						<Box
							sx={{
								backgroundColor: "#e0f7fa",
								padding: "12px",
								borderRadius: "12px",
								display: "flex",
								gap: "16px",
								alignItems: "center",
								position: "relative",
								width: isSmallScreen ? "60%" : "100%",
							}}
						>
							<Box
								component="p"
								sx={{ ...estiloP, width: "100%", margin: 0 }}
								dangerouslySetInnerHTML={{ __html: comprasHtml }}
							/>
						</Box>
					</Box>
				)}

				{(sumoTitle || sumoTextHtml) && (
					<Box data-block-id="text-5" sx={{ position: "relative" }}>
						<Box
							sx={{
								backgroundColor: "#ffe3cc",
								border: "3px solid #f28b35",
								borderRadius: "12px",
								padding: "8px 20px",
								fontSize: 12,
								fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
								color: "#333",
								width: "60%",
							}}
						>
							{sumoTitle && (
								<Box
									component="h4"
									sx={{
										margin: 0,
										marginBottom: "8px",
										fontSize: 13,
										fontWeight: 700,
										color: "#f28b35!important",
										textTransform: "uppercase",
										fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
									}}
								>
									{sumoTitle}
								</Box>
							)}
							{sumoTextHtml && (
								<Box
									component="p"
									sx={{
										fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
										fontSize: 13,
										color: "#333",
										margin: 0,
										"& a": {
											color: "#f28b35",
											fontWeight: 600,
											textDecoration: "underline",
										},
									}}
									dangerouslySetInnerHTML={{ __html: sumoTextHtml }}
								/>
							)}
						</Box>
						{sushiStickerImage && !sushiStickerError && (
							<img
								src={sushiStickerImage}
								alt=""
								onError={() => setSushiStickerError(true)}
								style={{
									width: 110,
									position: "absolute",
									bottom: isSmallScreen ? -30 : 0,
									right: -30,
								}}
							/>
						)}
						{stickerSushiHtml && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? "50px" : "35px",
									right: isSmallScreen ? 0 : "40px",
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: isSmallScreen ? 140 : 200,
									"& a": {
										color: "#caa100",
										textDecoration: "underline",
										fontWeight: "bold",
									},
								}}
								dangerouslySetInnerHTML={{ __html: stickerSushiHtml }}
							/>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template61.thumbnail = "/images/template-1.png";

export default Template61;
